import React, { ChangeEvent, FC, useCallback, useEffect, useMemo, useState } from "react";
import { Button, Checkbox, Heading, InfoSignIcon, Pane, Paragraph, SelectField } from "evergreen-ui";
import {
    AbstractTableViewProvider
} from "../../common/tables/view/AbstractTableViewProvider";
import { useTableViewContext } from "../../common/tables/view/AbstractTableViewProvider";
import { TableView } from "../../common/tables/view/TableView";
import { CostMetadataTableRow } from "../../../../common/tables/cost-metadata/CostMetadataTableRow";
import { CostMetadata, CostPayoutModifier, ShopifyProductVariant } from "../../../../common/models";
import { CostMetadataCreateInput } from "../../../../common/inputs/CostMetadataInput";
import { QueryInput } from "../../../../common/query-filters";
import {
    queryCostMetadataByShopifyProductId,
    useCreateCostMetadataMutation,
    useUpdateCostMetadataMutation,
} from "../../datastore/CostMetadataDataStore";
import { queryAllVariantsByProductListingId } from "../../datastore/ShopifyProductDataStore";
import { queryAllCostPayoutModifiers } from "../../datastore/CostPayoutModifierDataStore";
import { DefaultCostMetadataTableConfig } from "./cost-metadata/tables/config/DefaultCostMetadataTableConfig";
import { CostMetadataTableCellFactory } from "./cost-metadata/factory/CostMetadataTableCellFactory";
import { TableCellFactory } from "../../common/tables/factory";
import { EmptyState } from "../../common/EmptyState";
import { RampartAuthorized } from "../../rampart/RampartAuthorized";
import {
    CostMetadataTableRowSideSheetForm,
    ViewSideSheet,
} from "./cost-metadata/tables/CostMetadataTableRowSideSheetForm";

export type CostMetadataWithRelations = CostMetadata & {
    shopifyProductVariant?: ShopifyProductVariant[];
    costPayoutModifier?: CostPayoutModifier | null;
};

const VariantsViewSideSheet: FC<{
    isShown: boolean;
    variants: ShopifyProductVariant[];
    allVariants: ShopifyProductVariant[];
    costMetadataId: string;
    onDismiss: () => void;
    onSave: (costMetadataId: string, variantIds: string[]) => void;
}> = ({ isShown, variants, allVariants, costMetadataId, onDismiss, onSave }) => {
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [selectedVariantIds, setSelectedVariantIds] = useState<string[]>([]);

    useEffect(() => {
        if (isEditing) {
            setSelectedVariantIds(variants.map(v => String(v.id)));
        }
    }, [isEditing, variants]);

    useEffect(() => {
        if (!isShown) {
            setIsEditing(false);
        }
    }, [isShown]);

    const onVariantToggle = (variantId: string, checked: boolean) => {
        setSelectedVariantIds(prev =>
            checked ? [...prev, variantId] : prev.filter(id => id !== variantId)
        );
    };

    const handleSave = () => {
        onSave(costMetadataId, selectedVariantIds);
        setIsEditing(false);
    };

    // Combine currently linked variants + uncovered variants for the edit checklist
    const editableVariants = useMemo(() => {
        const variantMap = new Map<string, ShopifyProductVariant>();
        variants.forEach(v => variantMap.set(String(v.id), v));
        allVariants.forEach(v => variantMap.set(String(v.id), v));
        return Array.from(variantMap.values());
    }, [variants, allVariants]);

    return (
        <ViewSideSheet isShown={isShown} title={"Variants"} onDismiss={onDismiss}>
            {!isEditing ? (
                <>
                    <Pane marginBottom={16}>
                        <Button appearance={"primary"} onClick={() => setIsEditing(true)}>
                            Edit Variants
                        </Button>
                    </Pane>
                    {variants.length === 0 ? (
                        <Paragraph color={"muted"}>No variants linked</Paragraph>
                    ) : (
                        variants.map((variant: ShopifyProductVariant) => (
                            <Pane
                                key={variant.id}
                                padding={12}
                                marginBottom={8}
                                borderRadius={4}
                                border={"1px solid #e4e7eb"}
                            >
                                <Heading size={400}>{variant.sku || "No SKU"}</Heading>
                                <Paragraph size={300} color={"muted"}>{variant.title || "Untitled"}</Paragraph>
                                <Paragraph size={300} color={"muted"}>ID: {String(variant.id)}</Paragraph>
                            </Pane>
                        ))
                    )}
                </>
            ) : (
                <>
                    {editableVariants.length === 0 ? (
                        <Paragraph color={"muted"}>No variants available</Paragraph>
                    ) : (
                        editableVariants.map((variant: ShopifyProductVariant) => (
                            <Checkbox
                                key={variant.id}
                                label={`${variant.sku || "No SKU"} — ${variant.title || variant.id}`}
                                checked={selectedVariantIds.includes(String(variant.id))}
                                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                    onVariantToggle(String(variant.id), e.target.checked)
                                }
                            />
                        ))
                    )}
                    <Pane marginTop={16} display={"flex"} gap={8}>
                        <Button
                            appearance={"primary"}
                            onClick={handleSave}
                            disabled={selectedVariantIds.length === 0}
                        >
                            Save
                        </Button>
                        <Button onClick={() => setIsEditing(false)}>Cancel</Button>
                    </Pane>
                </>
            )}
        </ViewSideSheet>
    );
};

const ModifierViewSideSheet: FC<{
    isShown: boolean;
    modifier: CostPayoutModifier | null;
    costMetadataId: string;
    allModifiers: CostPayoutModifier[];
    onDismiss: () => void;
    onSave: (costMetadataId: string, costPayoutModifierId: string | null) => void;
}> = ({ isShown, modifier, costMetadataId, allModifiers, onDismiss, onSave }) => {
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [selectedModifierId, setSelectedModifierId] = useState<string>("");

    useEffect(() => {
        if (isEditing) {
            setSelectedModifierId(modifier?.id ?? "");
        }
    }, [isEditing, modifier]);

    useEffect(() => {
        if (!isShown) {
            setIsEditing(false);
        }
    }, [isShown]);

    const handleSave = () => {
        onSave(costMetadataId, selectedModifierId || null);
        setIsEditing(false);
    };

    return (
        <ViewSideSheet isShown={isShown} title={"Cost Payout Modifier"} onDismiss={onDismiss}>
            {!isEditing ? (
                <>
                    <Pane marginBottom={16}>
                        <Button appearance={"primary"} onClick={() => setIsEditing(true)}>
                            Change Modifier
                        </Button>
                    </Pane>
                    {modifier ? (
                        <Pane>
                            <Pane marginBottom={12}>
                                <Heading size={300}>Name</Heading>
                                <Paragraph>{modifier.name}</Paragraph>
                            </Pane>
                            <Pane marginBottom={12}>
                                <Heading size={300}>Country Code</Heading>
                                <Paragraph>{modifier.conditionCountryCode || <code style={{ color: "#8F95B2" }}>NULL</code>}</Paragraph>
                            </Pane>
                            <Pane marginBottom={12}>
                                <Heading size={300}>Priority</Heading>
                                <Paragraph>{modifier.priority ?? 0}</Paragraph>
                            </Pane>
                            <Pane marginBottom={12}>
                                <Heading size={300}>Source Name</Heading>
                                <Paragraph>{modifier.conditionSourceName || <code style={{ color: "#8F95B2" }}>NULL</code>}</Paragraph>
                            </Pane>
                            <Pane marginBottom={12}>
                                <Heading size={300}>Fulfillment Service</Heading>
                                <Paragraph>{modifier.conditionFulfillmentService || <code style={{ color: "#8F95B2" }}>NULL</code>}</Paragraph>
                            </Pane>
                            <Pane marginBottom={12}>
                                <Heading size={300}>Is Bundle</Heading>
                                <Paragraph>{modifier.conditionIsBundle === true ? "Yes" : modifier.conditionIsBundle === false ? "No" : <code style={{ color: "#8F95B2" }}>NULL</code>}</Paragraph>
                            </Pane>
                            <Pane marginBottom={12}>
                                <Heading size={300}>Email Domains</Heading>
                                <Paragraph>{modifier.conditionEmailDomains?.join(", ") || <code style={{ color: "#8F95B2" }}>NULL</code>}</Paragraph>
                            </Pane>
                        </Pane>
                    ) : (
                        <Paragraph color={"muted"}>No modifier data</Paragraph>
                    )}
                </>
            ) : (
                <>
                    <SelectField
                        label={"Cost Payout Modifier"}
                        value={selectedModifierId}
                        onChange={(e: ChangeEvent<HTMLSelectElement>) => setSelectedModifierId(e.target.value)}
                    >
                        <option value={""}>None</option>
                        {allModifiers.map(cpm => (
                            <option key={cpm.id} value={cpm.id}>
                                {cpm.name}
                            </option>
                        ))}
                    </SelectField>
                    <Pane marginTop={16} display={"flex"} gap={8}>
                        <Button appearance={"primary"} onClick={handleSave}>Save</Button>
                        <Button onClick={() => setIsEditing(false)}>Cancel</Button>
                    </Pane>
                </>
            )}
        </ViewSideSheet>
    );
};

/**
 * Bridge component rendered inside AbstractTableViewProvider to give
 * the view side sheets access to the provider's updateRow function.
 */
const CostMetadataSideSheetBridge: FC<{
    variantsSideSheet: { isShown: boolean; variants: ShopifyProductVariant[]; costMetadataId: string };
    modifiersSideSheet: { isShown: boolean; modifier: CostPayoutModifier | null; costMetadataId: string };
    costPayoutModifiers: CostPayoutModifier[];
    availableVariants: ShopifyProductVariant[];
    onDismissVariants: () => void;
    onDismissModifier: () => void;
    onSaveVariants: (costMetadataId: string, variantIds: string[], updateRow: (id: string | number, columnName: keyof CostMetadataTableRow, value: string | number | boolean | null, label?: string) => void) => void;
    onSaveModifier: (costMetadataId: string, costPayoutModifierId: string | null, updateRow: (id: string | number, columnName: keyof CostMetadataTableRow, value: string | number | boolean | null, label?: string) => void) => void;
}> = ({ variantsSideSheet, modifiersSideSheet, costPayoutModifiers, availableVariants, onDismissVariants, onDismissModifier, onSaveVariants, onSaveModifier }) => {
    const { updateRow } = useTableViewContext<CostMetadataTableRow, CostMetadata>();

    return (
        <>
            <VariantsViewSideSheet
                isShown={variantsSideSheet.isShown}
                variants={variantsSideSheet.variants}
                allVariants={availableVariants}
                costMetadataId={variantsSideSheet.costMetadataId}
                onDismiss={onDismissVariants}
                onSave={(costMetadataId, variantIds) => onSaveVariants(costMetadataId, variantIds, updateRow)}
            />
            <ModifierViewSideSheet
                isShown={modifiersSideSheet.isShown}
                modifier={modifiersSideSheet.modifier}
                costMetadataId={modifiersSideSheet.costMetadataId}
                allModifiers={costPayoutModifiers}
                onDismiss={onDismissModifier}
                onSave={(costMetadataId, costPayoutModifierId) => onSaveModifier(costMetadataId, costPayoutModifierId, updateRow)}
            />
        </>
    );
};

export const CostMetadataTab: FC<{ shopifyProductId?: string | null; productListingId: string }> = ({ shopifyProductId, productListingId }) => {
    if (!shopifyProductId) {
        return (
            <Pane padding={24}>
                <EmptyState
                    title="No Shopify product linked"
                    subtitle="This product listing is not linked to a Shopify product"
                >
                    <InfoSignIcon size={60} />
                </EmptyState>
            </Pane>
        );
    }

    return (
        <RampartAuthorized requiredGroups={["juniper.self-serve.admin"]}>
            <CostMetadataTableContent shopifyProductId={shopifyProductId} productListingId={productListingId} />
        </RampartAuthorized>
    );
};

const CostMetadataTableContent: FC<{ shopifyProductId: string; productListingId: string }> = ({ shopifyProductId, productListingId }) => {
    const defaultCreateInput: Partial<CostMetadataCreateInput> = useMemo(() => {
        return {
            variantIds: [],
        };
    }, []);

    const [showCreateRowForm, setShowCreateRowForm] = useState<boolean>(false);
    const [createInput, setCreateInput] = useState<Partial<CostMetadataCreateInput>>(defaultCreateInput);
    const [availableVariants, setAvailableVariants] = useState<ShopifyProductVariant[]>([]);
    const [costPayoutModifiers, setCostPayoutModifiers] = useState<CostPayoutModifier[]>([]);

    // Load all variants on mount (used by filter dropdown and create side sheet)
    useEffect(() => {
        queryAllVariantsByProductListingId(productListingId)
            .then(setAvailableVariants)
            .catch(() => setAvailableVariants([]));
    }, [productListingId]);

    // Variants cell-click side sheet state
    const [variantsSideSheet, setVariantsSideSheet] = useState<{ isShown: boolean; variants: ShopifyProductVariant[]; costMetadataId: string }>({
        isShown: false,
        variants: [],
        costMetadataId: "",
    });

    // Modifiers cell-click side sheet state
    const [modifiersSideSheet, setModifiersSideSheet] = useState<{ isShown: boolean; modifier: CostPayoutModifier | null; costMetadataId: string }>({
        isShown: false,
        modifier: null,
        costMetadataId: "",
    });

    // Load CPMs when create side sheet opens
    useEffect(() => {
        if (showCreateRowForm) {
            queryAllCostPayoutModifiers()
                .then(setCostPayoutModifiers)
                .catch(() => setCostPayoutModifiers([]));
        }
    }, [showCreateRowForm]);

    // Load CPMs when modifier side sheet opens in case of edit
    useEffect(() => {
        if (modifiersSideSheet.isShown) {
            queryAllCostPayoutModifiers()
                .then(setCostPayoutModifiers)
                .catch(() => setCostPayoutModifiers([]));
        }
    }, [modifiersSideSheet.isShown]);

    const onVariantsCellClick = useCallback((rowData: CostMetadata) => {
        const data = rowData as CostMetadataWithRelations;
        const variants = data.shopifyProductVariant ?? [];
        setVariantsSideSheet({
            isShown: true,
            variants,
            costMetadataId: rowData.id,
        });
    }, []);

    const onModifiersCellClick = useCallback((rowData: CostMetadata) => {
        const data = rowData as CostMetadataWithRelations;
        const modifier = data.costPayoutModifier ?? null;
        setModifiersSideSheet({
            isShown: true,
            modifier,
            costMetadataId: rowData.id,
        });
    }, []);

    const costMetadataTableCellFactory: TableCellFactory<CostMetadataTableRow, CostMetadata> = useMemo(() => {
        return new CostMetadataTableCellFactory({
            onVariantsCellClick,
            onModifiersCellClick,
            variantFilterOptions: availableVariants,
        });
    }, [onVariantsCellClick, onModifiersCellClick, availableVariants]);

    const queryTableData = useCallback((input: QueryInput<CostMetadataTableRow>) => {
        const variantSkusExpr = input.expressions.find(e => e.columnName === 'variantSkus');
        const startDateExpr = input.expressions.find(e => e.columnName === 'startDate');

        return queryCostMetadataByShopifyProductId({
            shopifyProductId,
            variantId: variantSkusExpr?.columnValue?.[0] as string | undefined,
            startDateFrom: startDateExpr?.columnValue?.[0] as string | undefined,
            startDateTo: startDateExpr?.columnValue?.[1] as string | undefined,
            options: {
                take: input.pageSize,
            },
        });
    }, [shopifyProductId]);

    const [
        createCostMetadata,
        {
            loading: createLoading,
            error: createError,
            called: createCalled,
        }
    ] = useCreateCostMetadataMutation();

    const [
        updateCostMetadata,
        {
            loading,
            called,
            error,
        }
    ] = useUpdateCostMetadataMutation();

    const onOpenSideSheet = useCallback(() => {
        setShowCreateRowForm(true);
    }, []);

    const onSubmitSideSheet = useCallback(() => {
        createCostMetadata(createInput as CostMetadataCreateInput);
    }, [createCostMetadata, createInput]);

    const onCancelSideSheet = useCallback(() => {
        setShowCreateRowForm(false);
    }, []);

    const NUMERIC_COLUMNS: Set<string> = useMemo(() => new Set([
        "unitCost", "juniperServiceFee", "fulfillmentFee", "processingFeePercent", "shippingSubsidy",
    ]), []);

    const onUpdateRow = useCallback((
        costMetadataList: CostMetadata[],
        id: string | number,
        columnName: keyof CostMetadataTableRow,
        value: string | number | boolean | null,
        label?: string,
    ): CostMetadata[] => {
        return costMetadataList.map((costMetadata: CostMetadata) => {
            if (costMetadata.id !== id) {
                return costMetadata;
            }

            const data = costMetadata as CostMetadataWithRelations;

            // Side sheet save: modifier change (routed through updateRow)
            if (columnName === "costPayoutModifierName" as keyof CostMetadataTableRow) {
                const costPayoutModifierId = value as string | null;
                updateCostMetadata({ id: costMetadata.id, costPayoutModifierId });
                const fullCpm = costPayoutModifiers.find(c => c.id === costPayoutModifierId) ?? null;
                return {
                    ...data,
                    costPayoutModifierId: costPayoutModifierId,
                    costPayoutModifier: fullCpm,
                } as CostMetadata;
            }

            // Side sheet save: variant reassignment (routed through updateRow)
            if (columnName === "variantSkus" as keyof CostMetadataTableRow) {
                const variantIds = value ? String(value).split(",") : [];
                const variantSkus = label ? label.split(",") : [];
                updateCostMetadata({ id: costMetadata.id, variantIds });
                return {
                    ...data,
                    shopifyProductVariant: variantIds.map((vid, i) => ({
                        id: vid,
                        sku: variantSkus[i] ?? vid,
                    } as unknown as ShopifyProductVariant)),
                } as CostMetadata;
            }

            // Regular inline edit
            let truncatedValue = value;
            if (NUMERIC_COLUMNS.has(columnName as string) && typeof value === "number") {
                truncatedValue = Math.round(value * 100) / 100;
            }

            const costMetadataToUpdate = {
                ...costMetadata,
                [columnName]: truncatedValue,
            };

            updateCostMetadata(costMetadataToUpdate);

            return costMetadataToUpdate;
        });
    }, [updateCostMetadata, costPayoutModifiers]);

    const emptyViewProps = useMemo(() => {
        return {
            primaryText: "No cost metadata found",
            secondaryText: "Add Cost Metadata",
            onClickSecondaryText: () => setShowCreateRowForm(true),
        };
    }, []);

    const errorViewProps = useMemo(() => {
        return {
            primaryText: "Failed to load cost metadata",
            secondaryText: "Refresh the page to try again",
        };
    }, []);

    // Reset form state after successful creation
    useEffect(() => {
        if (!createCalled) {
            return;
        }

        if (!createLoading && !createError) {
            setCreateInput(defaultCreateInput);
            setShowCreateRowForm(false);
        }
    }, [createLoading, createCalled, createError, defaultCreateInput]);

    const onSaveVariants = useCallback((costMetadataId: string, variantIds: string[], updateRow: (id: string | number, columnName: keyof CostMetadataTableRow, value: string | number | boolean | null, label?: string) => void) => {
        const skus = availableVariants.filter(v => variantIds.includes(String(v.id))).map(v => v.sku || String(v.id));
        updateRow(costMetadataId, "variantSkus" as keyof CostMetadataTableRow, variantIds.join(","), skus.join(","));
        setVariantsSideSheet({ isShown: false, variants: [], costMetadataId: "" });
    }, [availableVariants]);

    const onSaveModifier = useCallback((costMetadataId: string, costPayoutModifierId: string | null, updateRow: (id: string | number, columnName: keyof CostMetadataTableRow, value: string | number | boolean | null, label?: string) => void) => {
        const cpm = costPayoutModifiers.find(c => c.id === costPayoutModifierId);
        updateRow(costMetadataId, "costPayoutModifierName" as keyof CostMetadataTableRow, costPayoutModifierId, cpm?.name ?? "");
        setModifiersSideSheet({ isShown: false, modifier: null, costMetadataId: "" });
    }, [costPayoutModifiers]);

    return (
        <>
            <AbstractTableViewProvider
                queryTableData={queryTableData}
                onUpdateRow={onUpdateRow}
                updateRowLoading={loading}
                updateRowCalled={called}
                updateRowError={error}
                createRowCalled={createCalled}
                createRowError={createError}
                createRowLoading={createLoading}
                tableCellFactory={costMetadataTableCellFactory}
                savedTableConfigProps={{
                    tableName: "costMetadata",
                    identifiers: ["v1"],
                    defaultConfigValue: DefaultCostMetadataTableConfig,
                }}
            >
                <TableView
                    errorViewProps={errorViewProps}
                    emptyViewProps={emptyViewProps}
                />
                <CostMetadataSideSheetBridge
                    variantsSideSheet={variantsSideSheet}
                    modifiersSideSheet={modifiersSideSheet}
                    costPayoutModifiers={costPayoutModifiers}
                    availableVariants={availableVariants}
                    onDismissVariants={() => setVariantsSideSheet({ isShown: false, variants: [], costMetadataId: "" })}
                    onDismissModifier={() => setModifiersSideSheet({ isShown: false, modifier: null, costMetadataId: "" })}
                    onSaveVariants={onSaveVariants}
                    onSaveModifier={onSaveModifier}
                />
            </AbstractTableViewProvider>
            <CostMetadataTableRowSideSheetForm
                isShown={showCreateRowForm}
                costMetadata={createInput}
                variants={availableVariants}
                costPayoutModifiers={costPayoutModifiers}
                mutationLoading={createLoading}
                mutationCalled={createCalled}
                mutationError={createError}
                onDismiss={onCancelSideSheet}
                onOpen={onOpenSideSheet}
                onSubmit={onSubmitSideSheet}
                onInputChanged={setCreateInput}
            />
        </>
    );
};
